import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';

import {ConfigService} from '@ngmedax/config';
import {RegistryService} from '@ngmedax/registry';


@Injectable()
export class SettingsThemeService {

  public constructor(
    private http: HttpClient,
    private registryService: RegistryService,
    private configService: ConfigService
  ) {
  }

  /**
   * Posts settings data
   */
  public async save(data: any): Promise<any> {
    const headers = this.getAuthHeaders();
    const apiUrl = this.getSettingsApiUrl();

    if (!data.key) {
      const keys = Object.keys(data);
      for (const key of keys) {
        const value = typeof data[key] === 'object' ? JSON.stringify(data[key]) : data[key];
        await this.save({key, value});
      }
      return;
    }

    return await new Promise((resolve, reject) => {
      this.http.post(apiUrl, data, {headers}).subscribe(value => resolve(value), reject);
    });
  }

  /**
   * Saves theme
   */
  public async saveTheme(): Promise<any> {
    const headers = this.getAuthHeaders();
    const apiUrl = this.getThemeApiUrl('/mobile/save');

    return await new Promise((resolve, reject) => {
      this.http.get(apiUrl, {headers}).subscribe((result) => resolve(result), reject);
    });
  }

  /**
   * Returns settings data for given key
   */
  public async get(key: string): Promise<any> {
    const data = await this.save({key});
    if (data) {
      try {
        data.value = JSON.parse(data.value);
      } catch (error) {
        // do nothing
      }
    }

    return data;
  }

  /**
   * Reads a value with login only (/settings/get), so the administration of every user can apply instance settings.
   * Falls back to the settings endpoint with permissions for older apis. Resolves null for an empty value and
   * undefined when there is no settings api, no login or the request fails, so apps without these apis keep working
   */
  public async read(key: string): Promise<any> {
    const apiUrl = this.getSettingsApiUrl();
    if (!apiUrl || !this.registryService.get('session.authToken')) {
      return undefined;
    }

    const headers = this.getAuthHeaders();
    const post = (url: string) => new Promise<any>((resolve, reject) =>
      this.http.post(url, {key}, {headers}).subscribe(resolve, reject));

    let data: any;
    try {
      data = await post(`${apiUrl}/get`);
    } catch (error) {
      data = await post(apiUrl).catch(() => undefined);
    }

    if (data === undefined) {
      return undefined;
    }

    if (!data || data.value === undefined || data.value === null || data.value === '') {
      return null;
    }

    try {
      return JSON.parse(data.value);
    } catch (error) {
      return data.value;
    }
  }

  /**
   * Returns value for given key
   * @param {string} key
   */
  public async getValue(key: string): Promise<any> {
    const data = await this.get(key);
    return data ? data.value : null;
  }

  /**
   * Returns api url for settings
   *
   * @returns {string}
   */
  private getSettingsApiUrl(suffix: string = null): string {
    return this.buildUrl('apis.settings.uri', suffix);
  }

  /**
   * Returns api url for theme
   *
   * @returns {string}
   */
  private getThemeApiUrl(suffix: string = null): string {
    return this.buildUrl('apis.theme.uri', suffix);
  }

  /**
   * Builds url by value of given config key and suffix
   *
   * @param {string} configKey
   * @returns {any}
   */
  private buildUrl(configKey: string, suffix = null) {
    let baseUri = this.configService.get(configKey);

    if (suffix) {
      baseUri = `${baseUri}${suffix}`;
    }

    return baseUri;
  }

  /**
   * Returns auth headers by auth token and tenant id
   */
  private getAuthHeaders(): any {
    const headers: any = {};
    const authToken = this.registryService.get('session.authToken');
    const tenantId = this.registryService.get('session.tenantId');

    if (authToken) {
      headers['X-Api-Token'] = authToken;
    }

    if (tenantId) {
      headers['X-Api-TenantId'] = `${tenantId}`;
    }

    return headers;
  }
}
